/**
 * useCoachSteveSession
 *
 * Single source of truth for Coach Steve's conversation state. Replaces
 * the four scattered pending flags (pendingAction, pendingPlayerCreate,
 * pendingInjuryUpdate, pendingPracticeForFocus) with one discriminated
 * `pendingFlow`, while still exposing backwards-compatible getters and
 * setters so existing agent handlers continue to work unchanged.
 *
 * In addition the hook exposes:
 *  - `cancelFlow()`              — cancel any in-flight pending flow
 *  - `pendingInterrupt`          — interrupt prompt waiting for user
 *  - `requestInterrupt(...)`     — surface "pause flow X? switch to Y?"
 *  - `confirmInterrupt(true|false)` — resolve the prompt
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { CreatedPracticeInfo } from '../components/coach-steve/CoachStevePracticeForm';
import type {
  CreateGameInitialValues,
  PendingInjuryUpdate,
  PendingPlayerCreate,
} from '../components/coach-steve/agents';
import type {
  PendingAddDrillFollowUp,
  PendingGameLineupPick,
} from '../components/coach-steve/agents/types';

/** High-level pending UI action for inline forms. */
export type PendingFormAction = 'practice' | 'game_plan' | 'create_game';

/**
 * Discriminated union describing the user's currently-active flow. Only
 * one flow can be active at a time, which is exactly what the multi
 * sub-agent design wants. Legacy setters compose into this state.
 */
export type PendingFlow =
  | { kind: 'addPlayer'; data: PendingPlayerCreate }
  | { kind: 'injuryStatus'; data: PendingInjuryUpdate }
  | { kind: 'practiceFocus'; data: CreatedPracticeInfo }
  | { kind: 'addDrillFollowUp'; data: PendingAddDrillFollowUp }
  | { kind: 'gameLineupPick'; data: PendingGameLineupPick }
  | { kind: 'form'; action: PendingFormAction };

/** Pending interrupt prompt describing a candidate domain switch. */
export interface PendingInterrupt {
  /** Description of the flow the user is currently in. */
  currentFlowLabel: string;
  /** Description of the new intent that triggered the interrupt. */
  candidateLabel: string;
  /** Resume current flow when user declines the switch. */
  onDecline: () => void;
  /** Resolve the switch — typically cancels the current flow then dispatches the candidate intent. */
  onConfirm: () => void;
}

export interface CoachSteveSession {
  // Single source of truth
  pendingFlow: PendingFlow | null;
  /** True when ANY pending flow / form is currently active. */
  hasPendingFlow: boolean;
  /** Friendly label describing the active flow (router prompt + interrupt UX). */
  pendingFlowLabel: string | null;

  // Legacy getters (back-compat with agent handlers)
  pendingAction: PendingFormAction | null;
  pendingPlayerCreate: PendingPlayerCreate | null;
  pendingInjuryUpdate: PendingInjuryUpdate | null;
  pendingPracticeForFocus: CreatedPracticeInfo | null;
  pendingAddDrillFollowUp: PendingAddDrillFollowUp | null;
  pendingGameLineupPick: PendingGameLineupPick | null;
  createGameInitialValues: CreateGameInitialValues | null;

  // Legacy setters (back-compat)
  setPendingAction: (action: PendingFormAction | null) => void;
  setPendingPlayerCreate: (data: PendingPlayerCreate | null) => void;
  setPendingInjuryUpdate: (data: PendingInjuryUpdate | null) => void;
  setPendingPracticeForFocus: (info: CreatedPracticeInfo | null) => void;
  setPendingAddDrillFollowUp: (data: PendingAddDrillFollowUp | null) => void;
  setPendingGameLineupPick: (data: PendingGameLineupPick | null) => void;
  setCreateGameInitialValues: (vals: CreateGameInitialValues | null) => void;

  // New flow controls
  cancelFlow: () => void;
  pendingInterrupt: PendingInterrupt | null;
  requestInterrupt: (prompt: PendingInterrupt) => void;
  confirmInterrupt: (accept: boolean) => void;
}

const formActionLabels: Record<PendingFormAction, string> = {
  practice: 'creating a practice plan',
  game_plan: 'creating a game plan',
  create_game: 'adding a game to the schedule',
};

/** Human-readable description of a `PendingFlow`. */
export function describePendingFlow(flow: PendingFlow | null): string | null {
  if (!flow) return null;
  switch (flow.kind) {
    case 'addPlayer': {
      const { firstName, lastName } = flow.data;
      const name = [firstName, lastName].filter(Boolean).join(' ').trim();
      return name ? `adding ${name} to the roster` : 'adding a player';
    }
    case 'injuryStatus': {
      if (flow.data.kind === 'need_player') return 'updating an injury status (awaiting player)';
      return `updating ${flow.data.playerName}'s injury status`;
    }
    case 'practiceFocus':
      return `picking a focus for the ${flow.data.date} practice`;
    case 'addDrillFollowUp': {
      if (flow.data.phase === 'needDate') return 'choosing a practice date for your drill';
      return 'confirming whether to create a practice for your drill';
    }
    case 'gameLineupPick':
      return 'choosing which game to update for lineup / fielding';
    case 'form':
      return formActionLabels[flow.action];
  }
}

export function useCoachSteveSession(): CoachSteveSession {
  const [pendingFlow, setPendingFlow] = useState<PendingFlow | null>(null);
  const [createGameInitialValues, _setCreateGameInitialValues] = useState<CreateGameInitialValues | null>(null);
  const [pendingInterrupt, setPendingInterrupt] = useState<PendingInterrupt | null>(null);

  // Use a ref for derived legacy values that only need referential
  // equality (avoids re-rendering consumers that don't care).
  const flowRef = useRef(pendingFlow);
  flowRef.current = pendingFlow;

  // ---- Legacy getters ---------------------------------------------------
  const pendingPlayerCreate =
    pendingFlow?.kind === 'addPlayer' ? pendingFlow.data : null;
  const pendingInjuryUpdate =
    pendingFlow?.kind === 'injuryStatus' ? pendingFlow.data : null;
  const pendingPracticeForFocus =
    pendingFlow?.kind === 'practiceFocus' ? pendingFlow.data : null;
  const pendingAddDrillFollowUp =
    pendingFlow?.kind === 'addDrillFollowUp' ? pendingFlow.data : null;
  const pendingGameLineupPick =
    pendingFlow?.kind === 'gameLineupPick' ? pendingFlow.data : null;
  const pendingAction = pendingFlow?.kind === 'form' ? pendingFlow.action : null;

  // ---- Legacy setters ---------------------------------------------------
  const setPendingPlayerCreate = useCallback((data: PendingPlayerCreate | null) => {
    setPendingFlow(data ? { kind: 'addPlayer', data } : null);
  }, []);

  const setPendingInjuryUpdate = useCallback((data: PendingInjuryUpdate | null) => {
    setPendingFlow(data ? { kind: 'injuryStatus', data } : null);
  }, []);

  const setPendingPracticeForFocus = useCallback((info: CreatedPracticeInfo | null) => {
    setPendingFlow(info ? { kind: 'practiceFocus', data: info } : null);
  }, []);

  const setPendingAddDrillFollowUp = useCallback((data: PendingAddDrillFollowUp | null) => {
    setPendingFlow(data ? { kind: 'addDrillFollowUp', data } : null);
  }, []);

  const setPendingGameLineupPick = useCallback((data: PendingGameLineupPick | null) => {
    setPendingFlow(data ? { kind: 'gameLineupPick', data } : null);
  }, []);

  const setPendingAction = useCallback((action: PendingFormAction | null) => {
    if (action === null) {
      setPendingFlow(null);
      _setCreateGameInitialValues(null);
      return;
    }
    setPendingFlow({ kind: 'form', action });
  }, []);

  const setCreateGameInitialValues = useCallback((vals: CreateGameInitialValues | null) => {
    _setCreateGameInitialValues(vals);
  }, []);

  // ---- Flow controls ----------------------------------------------------
  const cancelFlow = useCallback(() => {
    setPendingFlow(null);
    _setCreateGameInitialValues(null);
  }, []);

  const requestInterrupt = useCallback((prompt: PendingInterrupt) => {
    setPendingInterrupt(prompt);
  }, []);

  const confirmInterrupt = useCallback((accept: boolean) => {
    setPendingInterrupt((prev) => {
      if (!prev) return null;
      if (accept) prev.onConfirm();
      else prev.onDecline();
      return null;
    });
  }, []);

  const pendingFlowLabel = useMemo(() => describePendingFlow(pendingFlow), [pendingFlow]);

  return {
    pendingFlow,
    hasPendingFlow: pendingFlow !== null,
    pendingFlowLabel,
    pendingAction,
    pendingPlayerCreate,
    pendingInjuryUpdate,
    pendingPracticeForFocus,
    pendingAddDrillFollowUp,
    pendingGameLineupPick,
    createGameInitialValues,
    setPendingAction,
    setPendingPlayerCreate,
    setPendingInjuryUpdate,
    setPendingPracticeForFocus,
    setPendingAddDrillFollowUp,
    setPendingGameLineupPick,
    setCreateGameInitialValues,
    cancelFlow,
    pendingInterrupt,
    requestInterrupt,
    confirmInterrupt,
  };
}
